<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  batch: { type: Object, required: true },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['send', 'discard', 'update']);
const { t } = useI18n();
const editing = ref(false);
const showMenu = ref(false);
const content = ref('');
const composedContent = computed(
  () =>
    props.batch.content ||
    props.batch.fragments?.map(item => item.content).join('\n\n') ||
    ''
);
const beginEdit = () => {
  content.value = composedContent.value;
  editing.value = true;
};
const save = () => {
  emit('update', content.value);
  editing.value = false;
};
const menuItems = computed(() => [
  {
    action: 'edit',
    value: 'edit',
    label: t('KAPTION_PLATFORM.MESSAGE_BATCH.EDIT'),
    icon: 'i-lucide-pencil',
  },
  {
    action: 'delete',
    value: 'discard',
    label: t('KAPTION_PLATFORM.MESSAGE_BATCH.DISCARD'),
    icon: 'i-lucide-trash-2',
  },
]);
const handleMenuAction = ({ value }) => {
  showMenu.value = false;
  if (value === 'edit') beginEdit();
  if (value === 'discard') emit('discard');
};
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - One evolving, non-delivered outbound bubble. -->
  <li class="my-3 flex justify-end" aria-live="polite" aria-atomic="true">
    <article
      class="w-full max-w-lg rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm bg-n-green-3 text-n-slate-12"
    >
      <div class="px-4 py-3">
        <textarea
          v-if="editing"
          v-model="content"
          class="w-full resize-y rounded-md border border-n-strong bg-n-background p-2 text-sm"
          rows="4"
          :aria-label="$t('KAPTION_PLATFORM.MESSAGE_BATCH.EDIT_LABEL')"
        />
        <p v-else class="whitespace-pre-wrap text-sm leading-6">
          {{ composedContent }}
        </p>
      </div>
      <footer
        class="flex min-h-9 items-center justify-between gap-3 border-t border-n-weak px-4 py-1.5 text-xs"
      >
        <div class="flex items-center gap-2 text-n-slate-11">
          <span class="size-1.5 rounded-full bg-n-green-9 animate-pulse" />
          <span>{{
            $t('KAPTION_PLATFORM.MESSAGE_BATCH.COMBINING', {
              current: batch.fragment_count,
              target: batch.target_count,
            })
          }}</span>
        </div>
        <div class="flex items-center gap-1">
          <template v-if="editing">
            <button
              type="button"
              class="rounded-md px-2 py-1 text-n-slate-11 hover:bg-n-alpha-black4"
              @click="editing = false"
            >
              {{ $t('KAPTION_PLATFORM.MESSAGE_BATCH.CANCEL') }}
            </button>
            <button
              type="button"
              class="rounded-md px-2 py-1 font-medium text-n-green-11 hover:bg-n-alpha-black4"
              :disabled="busy || !content.trim()"
              @click="save"
            >
              {{ $t('KAPTION_PLATFORM.MESSAGE_BATCH.SAVE') }}
            </button>
          </template>
          <template v-else>
            <button
              type="button"
              class="rounded-md px-2 py-1 font-medium text-n-green-11 hover:bg-n-alpha-black4"
              :disabled="busy"
              @click="emit('send')"
            >
              {{ $t('KAPTION_PLATFORM.MESSAGE_BATCH.SEND_NOW') }}
            </button>
            <div
              v-on-clickaway="() => (showMenu = false)"
              class="relative flex items-center"
            >
              <Button
                icon="i-lucide-ellipsis-vertical"
                color="slate"
                variant="ghost"
                size="xs"
                :aria-label="$t('KAPTION_PLATFORM.MESSAGE_BATCH.MORE_ACTIONS')"
                :disabled="busy"
                @click="showMenu = !showMenu"
              />
              <DropdownMenu
                v-if="showMenu"
                :menu-items="menuItems"
                class="bottom-full mb-1 ltr:right-0 rtl:left-0"
                @action="handleMenuAction"
              />
            </div>
          </template>
        </div>
      </footer>
    </article>
  </li>
</template>
